import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminRole } from '../../hooks/useAdminRole';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Link,
  TextField,
  Button,
  Alert,
  Rating,
  Tooltip,
  IconButton,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CategoryManager from './CategoryManager';
import UsersManagement from './UsersManagement';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';

interface HomeComment {
  author: string;
  text: string;
  rating: number;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAdminRole();
  const [email, setEmail] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  
  // État pour les commentaires
  const [comments, setComments] = useState<HomeComment[]>([
    { author: '', text: '', rating: 0 },
    { author: '', text: '', rating: 0 },
    { author: '', text: '', rating: 0 }
  ]);

  // Charger les commentaires depuis Firebase
  const loadComments = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'homeComments');
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const loadedComments: HomeComment[] = [
          data.comment1 || { author: '', text: '', rating: 0 },
          data.comment2 || { author: '', text: '', rating: 0 },
          data.comment3 || { author: '', text: '', rating: 0 }
        ];
        setComments(loadedComments);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires:", error);
      setSaveStatus({
        type: 'error',
        message: 'Erreur lors du chargement des commentaires'
      });
    }
  };

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  // Charger l'email initial
  useEffect(() => {
    const loadEmailFromFirebase = async () => {
      try {
        // On lit directement le document 'email' dans la collection 'settings'
        const emailDoc = await getDoc(doc(db, 'settings', 'email'));
        
        if (emailDoc.exists()) {
          const savedEmail = emailDoc.data().correspondenceEmail || '';
          console.log('Email chargé:', savedEmail); // Debug
          setEmail(savedEmail);
          setCurrentEmail(savedEmail);
        } else {
          console.log('Document email non trouvé'); // Debug
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'email:', error);
      }
    };

    if (isAdmin) {
      loadEmailFromFirebase();
      loadComments(); // Charger les commentaires au montage
    }
  }, [isAdmin]);

  const handleEmailSave = async () => {
    try {
      // On écrit directement dans le document 'email' de la collection 'settings'
      await setDoc(doc(db, 'settings', 'email'), {
        correspondenceEmail: email,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid || ''
      });

      setCurrentEmail(email);
      setSaveStatus({ type: 'success', message: 'Email de correspondance mis à jour avec succès' });
    } catch (error) {
      console.error('Erreur:', error);
      setSaveStatus({ type: 'error', message: 'Erreur lors de la mise à jour de l\'email' });
    }

    setTimeout(() => {
      setSaveStatus(null);
    }, 3000);
  };

  // Fonction pour mettre à jour un commentaire
  const handleCommentChange = (index: number, field: keyof HomeComment, value: string | number) => {
    const newComments = [...comments];
    newComments[index] = { ...newComments[index], [field]: value };
    setComments(newComments);
  };

  // Fonction pour sauvegarder un commentaire
  const handleSaveComment = async (index: number) => {
    try {
      const settingsRef = doc(db, 'settings', 'homeComments');
      const comment = comments[index];
      
      await setDoc(settingsRef, {
        [`comment${index + 1}`]: {
          author: comment.author,
          text: comment.text,
          rating: comment.rating
        }
      }, { merge: true });

      setSaveStatus({
        type: 'success',
        message: `Commentaire ${index + 1} sauvegardé avec succès`
      });

      // Effacer le message après 3 secondes
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);

    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: `Erreur lors de la sauvegarde du commentaire ${index + 1}`
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Container maxWidth="xl">
      {/* Ajout d'un style global pour les ancres */}
      <Box sx={{
        '& [id]::before': {
          content: '""',
          display: 'block',
          height: '100px', // Ajustez cette valeur selon la hauteur de votre header
          marginTop: '-80px',
          visibility: 'hidden',
          
        }
      }}>
        {/* Menu de navigation */}
        <Paper 
          sx={{ 
            p: 3,
            mt: 10,
            mb: 1,
            backgroundColor: '#1a1b35',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            borderRadius: 1,
          }}
        >
          <Stack 
            direction="row" 
            spacing={3} 
            sx={{ 
              justifyContent: 'center',
              '& a': {
                color: '#68f1d1',
                textDecoration: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                }
              }
            }}
          >
            <Link href="#categories">Catégories</Link>
            <Link href="#email">Email</Link>
            <Link href="#users">Users</Link>
            <Link href="#reviews">Commentaires</Link>
          </Stack>
        </Paper>

        {/* Section Catégories */}
        <Box id="categories" >
          <Paper 
            sx={{ 
              p: 3,
              mb: 4,
              background: 'var(--Uni1-greycool)',
              backgroundColor: '#1a1b35',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#68f1d1',
                  fontWeight: 600,
                }}
              >
                Gestion des catégories
              </Typography>
              <Tooltip title="Retour au menu" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    ml: 2,
                    color: '#68f1d1',
                    '&:hover': {
                      backgroundColor: 'rgba(104, 241, 209, 0.1)',
                    }
                  }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <CategoryManager />
          </Paper>
        </Box>

        {/* Section Email */}
        <Box id="email">
          <Paper 
            sx={{ 
              p: 3,
              mb: 4,
              backgroundColor: '#1a1b35',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#68f1d1',
                  fontWeight: 600,
                }}
              >
                Email de correspondance
              </Typography>
              <Tooltip title="Retour au menu" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    ml: 2,
                    color: '#68f1d1',
                    '&:hover': {
                      backgroundColor: 'rgba(104, 241, 209, 0.1)',
                    }
                  }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ maxWidth: 500 }}>
              {saveStatus && (
                <Alert 
                  severity={saveStatus.type}
                  sx={{ mb: 2 }}
                >
                  {saveStatus.message}
                </Alert>
              )}

              {currentEmail && (
                <Typography 
                  sx={{ 
                    mb: 2,
                    color: 'rgba(104, 241, 209, 0.7)',
                  }}
                >
                  Email actuel : {currentEmail}
                </Typography>
              )}
              
              <TextField
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#68f1d1',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(104, 241, 209, 0.3)',
                      borderRadius: '12px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(104, 241, 209, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#68f1d1',
                    },
                    backgroundColor: 'rgba(26, 27, 46, 0.5)',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(104, 241, 209, 0.7)',
                  },
                }}
              />
              
              <Button
                onClick={handleEmailSave}
                disabled={email === currentEmail}
                sx={{
                  backgroundColor: '#68f1d1',
                  color: '#1a1b2e',
                  '&:hover': {
                    backgroundColor: '#5fdfb4',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(104, 241, 209, 0.3)',
                  },
                }}
              >
                Sauvegarder
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Section Users */}
        <Box id="users">
          <Paper 
            elevation={3}
            sx={{ 
              p: 3,
              mb: 4,
              backgroundColor: '#1a1b35',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#68f1d1',
                  fontWeight: 600,
                }}
              >
                Gestion des utilisateurs
              </Typography>
              <Tooltip title="Retour au menu" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    ml: 2,
                    color: '#68f1d1',
                    '&:hover': {
                      backgroundColor: 'rgba(104, 241, 209, 0.1)',
                    }
                  }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <UsersManagement />
          </Paper>
        </Box>

        {/* Section Commentaires */}
        <Box id="reviews">
          <Paper 
            elevation={3}
            sx={{ 
              p: 3,
              mb: 4,
              backgroundColor: '#1a1b35',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#68f1d1',
                  fontWeight: 600,
                }}
              >
                Commentaires de la page d'accueil
              </Typography>
              <Tooltip title="Retour au menu" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    ml: 2,
                    color: '#68f1d1',
                    '&:hover': {
                      backgroundColor: 'rgba(104, 241, 209, 0.1)',
                    }
                  }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',              // 1 colonne sur mobile
                  sm: '1fr 1fr',          // 2 colonnes sur tablette
                  md: '1fr 1fr 1fr',      // 3 colonnes sur desktop
                },
                gap: 3,
              }}
            >
              {[0, 1, 2].map((index) => (
                <Paper 
                  key={index}
                  elevation={2}
                  sx={{ 
                    p: 2,
                    backgroundColor: 'rgba(26, 27, 53, 0.8)',
                    border: '1px solid rgba(104, 241, 209, 0.2)',
                    borderRadius: '22px',
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#68f1d1',
                      mb: 1
                    }}
                  >
                    Commentaire {index + 1}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Nom de l'auteur"
                    value={comments[index].author}
                    onChange={(e) => handleCommentChange(index, 'author', e.target.value)}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: 'rgba(104, 241, 209, 0.2)',
                          borderRadius: '12px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(104, 241, 209, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#68f1d1',
                        },
                      },
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="Entrez votre commentaire ici..."
                    value={comments[index].text}
                    onChange={(e) => handleCommentChange(index, 'text', e.target.value)}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: 'rgba(104, 241, 209, 0.2)',
                          borderRadius: '12px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(104, 241, 209, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#68f1d1',
                        },
                      },
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography 
                      sx={{ 
                        color: 'white',
                        mr: 1
                      }}
                    >
                      Note :
                    </Typography>
                    <Rating
                      value={comments[index].rating}
                      onChange={(_, newValue) => handleCommentChange(index, 'rating', newValue || 0)}
                      size="large"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: 'var(--star-icons)',
                        },
                        '& .MuiRating-iconEmpty': {
                          color: 'rgba(104, 241, 209, 0.2)',
                        },
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    onClick={() => handleSaveComment(index)}
                    sx={{
                      backgroundColor: '#68f1d1',
                      color: '#1a1b2e',
                      '&:hover': {
                        backgroundColor: '#5fdfb4',
                      },
                      width: '100%',
                    }}
                  >
                    Sauvegarder
                  </Button>
                </Paper>
              ))}
            </Box>

            {saveStatus && (
              <Alert 
                severity={saveStatus.type}
                sx={{ 
                  mt: 2,
                  backgroundColor: saveStatus.type === 'success' ? 'rgba(104, 241, 209, 0.1)' : 'rgba(255, 72, 66, 0.1)',
                  color: saveStatus.type === 'success' ? '#68f1d1' : '#ff4842',
                  '& .MuiAlert-icon': {
                    color: saveStatus.type === 'success' ? '#68f1d1' : '#ff4842',
                  },
                }}
              >
                {saveStatus.message}
              </Alert>
            )}
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}
